import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useLocation } from 'wouter';
import { User, LoginCredentials, RegisterCredentials, ImpersonationState } from '@/types/auth';
import { apiClient } from '@/lib/api-client';
import { authService } from '@/services/api';
import type { GoogleExchangeResult } from '@/services/api/auth.service';
import { organizationService } from '@/services/api/organization.service';
import { platformService } from '@/services/api/platform.service';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import Cookies from 'js-cookie';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogleCode: (code: string) => Promise<GoogleExchangeResult>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  resendConfirmationEmail: (email: string) => Promise<void>;
  clearError: () => void;
  impersonation: ImpersonationState | null;
  startImpersonation: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  const [, setLocation] = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      const accessToken = Cookies.get('access_token');

      // No tenant session: either signed out, or signed in to the platform
      // console, whose token /auth/me does not accept.
      if (!accessToken || apiClient.isPlatformSession) {
        setUser(null);
        setImpersonation(null);
        setLoading(false);
        return;
      }

      const session = await authService.getSession();
      setUser(session.user);
      setImpersonation(session.impersonation);
      setLoading(false);
    } catch (error) {
      // Only clear auth if it's an actual auth error, not network issue
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 401) {
          apiClient.clearAuth();
          setUser(null);
        }
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      setLoading(true);

      const { user, accessToken, refreshToken } = await authService.login(credentials);

      // Set tokens first and ensure they're persisted
      apiClient.setAuthTokens(accessToken, refreshToken);

      // Verify tokens are set before proceeding
      const tokenSet = Cookies.get('access_token');

      if (!tokenSet) {
        throw new Error('Authentication tokens were not properly set');
      }

      setUser(user);

      // Small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 100));

      await redirectAfterLogin(user);
    } catch (error: any) {
      const message = sanitizeErrorMessage(error, 'Login failed');
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Shared post-login redirect: admins are checked for pending onboarding,
  // everyone else goes to their role's home page
  const redirectAfterLogin = async (user: User) => {
    if (user.role === 'admin') {
      try {
        const { onboarding } = await organizationService.getOnboardingStatus();
        if (!onboarding.completed && !onboarding.skipped) {
          setLocation('/admin/onboarding');
          return;
        }
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Continue with normal redirect if onboarding check fails
      }
    }

    const redirectPath = user.role === 'admin' ? '/admin/dashboard' :
                        user.role === 'staff' ? '/staff/dashboard' : '/';
    setLocation(redirectPath);
  };

  const loginWithGoogleCode = async (code: string): Promise<GoogleExchangeResult> => {
    try {
      setError(null);
      setLoading(true);

      const result = await authService.exchangeGoogleCode(code);

      const { user, accessToken, refreshToken, newUser } = result;
      apiClient.setAuthTokens(accessToken, refreshToken);
      setUser(user);

      // newUser comes from the exchange response — it used to be read off the
      // redirect URL, where anyone could set it.
      if (newUser && user.role === 'admin') {
        setLocation('/admin/onboarding');
        return result;
      }

      await redirectAfterLogin(user);
      return result;
    } catch (error: any) {
      const message = sanitizeErrorMessage(error, 'Google sign-in failed');
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      setError(null);
      setLoading(true);
      const { user, accessToken, refreshToken } = await authService.register(credentials);

      apiClient.setAuthTokens(accessToken, refreshToken);
      setUser(user);

      // For admin users (new organizations), redirect to onboarding
      if (user.role === 'admin') {
        // New registrations should always go to onboarding
        setLocation('/admin/onboarding');
      } else {
        setLocation('/');
      }
    } catch (error: any) {
      const message = sanitizeErrorMessage(error, 'Registration failed');
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
    } catch {
      // Ignore logout errors
    } finally {
      apiClient.clearAuth();
      setUser(null);
      setLocation('/');
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      setUser(user);
    } catch {
      setUser(null);
    }
  };

  const resendConfirmationEmail = async (email: string) => {
    try {
      setError(null);
      await apiClient.instance.post('/v1/auth/resend-confirmation', { email });
    } catch (error: any) {
      const message = sanitizeErrorMessage(error, 'Failed to resend confirmation email');
      setError(message);
      throw new Error(message);
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Super-admin: begin impersonating a target user. The platform token is
  // stashed by apiClient.enterImpersonation; @current_user becomes the target.
  const startImpersonation = async (userId: number) => {
    const result = await platformService.impersonate(userId);
    apiClient.enterImpersonation(result.accessToken);
    const session = await authService.getSession();
    setUser(session.user);
    setImpersonation(session.impersonation);

    const role = session.user.role;
    const redirectPath = role === 'admin' ? '/admin/dashboard' :
                        role === 'staff' ? '/staff/dashboard' : '/';
    setLocation(redirectPath);
  };

  // End impersonation: close the audit record (using the stashed platform token),
  // restore the platform session, and return to the picker.
  const stopImpersonation = async () => {
    const sessionId = impersonation?.sessionId;
    const platformToken = apiClient.getPlatformToken();
    if (sessionId && platformToken) {
      try {
        await platformService.stopImpersonation(sessionId, platformToken);
      } catch {
        // Ignore — the token expires on its own regardless.
      }
    }
    apiClient.exitImpersonation();
    setImpersonation(null);
    setUser(null);
    setLocation('/platform/users');
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    loginWithGoogleCode,
    register,
    logout,
    refreshUser,
    resendConfirmationEmail,
    clearError,
    impersonation,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Custom hook for protected routes
export function useRequireAuth(requiredRole?: User['role']) {
  const { user, loading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        setLocation('/login');
      } else if (requiredRole && user?.role !== requiredRole) {
        setLocation('/unauthorized');
      }
    }
  }, [loading, isAuthenticated, user, requiredRole, setLocation]);

  return { user, loading, isAuthenticated };
}
